import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FlightIcon from '@mui/icons-material/Flight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import DraftsIcon from '@mui/icons-material/Drafts';
import DisciplineAccessControl from '../common/DisciplineAccessControl';
import CreateAerialInspectionModal from './modals/CreateAerialInspectionModal';
import AerialInspectionDetailsModal from './modals/AerialInspectionDetailsModal';
import EditAerialInspectionModal from './modals/EditAerialInspectionModal';

const AerialDefectsPage = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const handleCreateNewInspection = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenCreateDialog(false);
  };

  // Fetch aerial inspections
  useEffect(() => {
    let cancelled = false;
    const fetchRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const { default: api } = await import('../../services/api');
        const { useAuth } = await import('../../context/AuthContext');
        // dynamic import hook value via module is not possible; instead, rely on backend to scope by token
        const resp = await api.get('/aerial-inspections');
        if (!cancelled) setRows(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        if (!cancelled) setError('Failed to load aerial inspections');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRows();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const handleCreated = () => {
    setRefreshKey(k => k + 1);
    handleCloseDialog();
  };

  const handleUpdated = () => {
    setRefreshKey(k => k + 1);
  };

  // Helper function to get status chip
  const getStatusChip = (status) => {
    const statusConfig = {
      draft: { 
        label: 'Draft', 
        color: 'default',
        icon: <DraftsIcon fontSize="inherit" />
      },
      in_progress: { 
        label: 'In Progress', 
        color: 'warning',
        icon: <PendingIcon fontSize="inherit" />
      },
      completed: { 
        label: 'Completed', 
        color: 'success',
        icon: <CheckCircleIcon fontSize="inherit" />
      },
    };

    const config = statusConfig[status] || { 
      label: status || 'Unknown', 
      color: 'default',
      icon: <PendingIcon fontSize="inherit" />
    };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
        icon={config.icon}
      />
    );
  };

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
      <Box sx={{ padding: 3, maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
              alignItems: 'flex-start',
            flexWrap: 'wrap',
              gap: 2,
              mb: 2
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Aerial-View Defects
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              AI-powered aerial defect detection and validation system from drone inspections
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNewInspection}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1.2,
              backgroundColor: theme.palette.primary.main,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
              }
            }}
          >
            Create New Inspection
          </Button>
        </Box>

          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>
              Showing {rows.length} inspection session{rows.length === 1 ? '' : 's'}
            </span>
            </Typography>
          </Box>

        {/* Loading State */}
                {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading aerial inspections...
            </Typography>
          </Box>
        )}

        {/* Error State */}
                {error && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error">
              Error Loading Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setRefreshKey(k => k + 1)}
            >
              Retry
            </Button>
          </Box>
        )}

        {/* Table Content */}
        {!loading && !error && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Aerial Inspection Sessions
            </Typography>
            
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: '12px',
                boxShadow: theme.shadows[2],
                overflow: 'hidden',
                mb: 3
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="aerial inspections table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Inspection Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Inspection Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Inspector</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cycle</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '120px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <FlightIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                          No aerial inspections yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Create your first inspection to get started
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow
                        key={row.id || index}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                          transition: 'background-color 0.2s ease-in-out'
                        }}
                        hover
                        onClick={() => { 
                          setSelectedInspection(row); 
                          setDetailsOpen(true); 
                        }}
                      >
                        <TableCell>
                          <Chip 
                            label={`AI-${row.id}`}
                            icon={<FlightIcon fontSize="small" />}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': {
                                color: theme.palette.info.main,
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.inspection_date 
                              ? new Date(row.inspection_date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.title || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.inspection_type_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {row.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.inspector_name || row.inspector_id || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.cycle || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(row.status)}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInspection(row);
                                  setDetailsOpen(true);
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Inspection">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInspection(row);
                                  setEditOpen(true);
                                }}
                                sx={{
                                  color: theme.palette.secondary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Create New Inspection Modal */}
        {openCreateDialog && (
          <CreateAerialInspectionModal
            open={openCreateDialog}
            onClose={handleCloseDialog}
            onSuccess={handleCreated}
          />
        )}
        {detailsOpen && selectedInspection && (
          <AerialInspectionDetailsModal
            open={detailsOpen}
            onClose={() => { setDetailsOpen(false); setSelectedInspection(null); }}
            inspection={selectedInspection}
            onDataMutated={() => setRefreshKey(k => k + 1)}
          />
        )}
        {editOpen && selectedInspection && (
          <EditAerialInspectionModal
            open={editOpen}
            onClose={() => { setEditOpen(false); setSelectedInspection(null); }}
            inspection={selectedInspection}
            onUpdated={() => {
              setEditOpen(false);
              setSelectedInspection(null);
              handleUpdated();
            }}
          />
        )}
      </Box>
    </DisciplineAccessControl>
  );
};

export default AerialDefectsPage;

